import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Card, Stack, Text } from '@mantine/core';
import type { Icon } from '@tabler/icons-react';
import {
  IconAdjustments, IconBell, IconChevronLeft, IconDeviceMobile, IconKey, IconLayoutGrid, IconLock,
  IconPlug, IconRobot, IconSettings, IconShieldCheck, IconShieldLock, IconUser,
} from '@tabler/icons-react';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { mw, mwSectionLabel } from '@/shared/mobile/tokens';
import { useCanManageApiKeys } from '@/features/api-keys/useCanManageApiKeys';
import { useAiAssistantsVisible } from '@/features/mcp/useMcpServerInfo';

export const Route = createFileRoute('/_app/settings')({ component: SettingsLayout });

interface NavItem { to: string; label: string; hint: string; icon: Icon; end?: boolean }

const NAV: NavItem[] = [
  { to: '/settings', label: 'Panoramica', hint: 'Tutte le sezioni', icon: IconLayoutGrid, end: true },
  { to: '/settings/profile', label: 'Profilo', hint: 'Anagrafica e avatar', icon: IconUser },
  { to: '/settings/preferences', label: 'Preferenze', hint: 'Lingua, tema, fuso orario', icon: IconAdjustments },
  { to: '/settings/notification-preferences', label: 'Notifiche', hint: 'Canali e categorie', icon: IconBell },
  { to: '/settings/security', label: 'Sicurezza', hint: 'Password e accessi', icon: IconLock },
  { to: '/settings/devices', label: 'Dispositivi', hint: 'Sessioni e device attivi', icon: IconDeviceMobile },
  { to: '/settings/api-keys', label: 'API Keys', hint: 'Token integrazione', icon: IconKey },
  { to: '/settings/ai-assistants', label: 'Assistenti AI', hint: 'Claude collegato ai tuoi dati', icon: IconRobot },
  { to: '/settings/integrations', label: 'Integrazioni', hint: 'Webhook e servizi esterni', icon: IconPlug },
  { to: '/settings/privacy', label: 'Privacy / GDPR', hint: 'Export dati e cancellazione', icon: IconShieldCheck },
  { to: '/settings/protected-data', label: 'Dati particolari', hint: 'Status protetti registrati su di te', icon: IconShieldLock },
];

function SettingsLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const isMobile = useIsMobileShell();
  const canApiKeys = useCanManageApiKeys();
  const aiAssistants = useAiAssistantsVisible();
  const visible = (to: string) =>
    (to !== '/settings/api-keys' || canApiKeys) && (to !== '/settings/ai-assistants' || aiAssistants);

  // ─── Mobile: header di schermata (con hamburger/drawer) sull'indice, back-breadcrumb
  //     sulle sotto-pagine; contenuto a piena larghezza (niente due-colonne desktop). ───
  if (isMobile) {
    const isIndex = path === '/settings' || path === '/settings/';
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        {isIndex ? (
          <MobileHeader title="Impostazioni" />
        ) : (
          <button
            type="button"
            onClick={() => navigate({ to: '/settings' })}
            className="mw-pressable"
            style={{
              display: 'flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
              border: 0, background: 'transparent', cursor: 'pointer', padding: '4px 0',
              color: mw.accentText, fontWeight: 600, fontSize: 'var(--fs-body)', fontFamily: 'inherit',
            }}
          >
            <IconChevronLeft size={18} /> Impostazioni
          </button>
        )}
        <Outlet />
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconSettings size={22} />}
        title="Impostazioni"
        sub="Account, sicurezza, notifiche e integrazioni."
      />

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Navigazione sezioni (sticky) */}
        <Card
          withBorder
          radius="lg"
          shadow="sm"
          padding="sm"
          style={{ flex: '1 1 240px', maxWidth: 300, position: 'sticky', top: 16 }}
        >
          <Text style={{ ...mwSectionLabel, margin: '4px 8px 8px' }}>Sezioni</Text>
          <Stack gap={4}>
            {NAV.filter((it) => visible(it.to)).map((it) => {
              const active = it.end ? path === it.to : path.startsWith(it.to);
              const Ico = it.icon;
              return (
                <Link key={it.to} to={it.to} style={{ textDecoration: 'none' }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, borderRadius: 11, padding: '10px 12px',
                      background: active ? mw.brandTint : 'transparent',
                      borderLeft: `3px solid ${active ? mw.brand : 'transparent'}`,
                    }}
                  >
                    <span style={{
                      width: 30, height: 30, borderRadius: 9, flexShrink: 0, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: active ? mw.brandFill : mw.surface2,
                      color: active ? 'var(--on-brand)' : mw.ink500,
                    }}>
                      <Ico size={16} />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: 'var(--fs-compact)', color: active ? mw.brand : mw.ink900 }}>
                        {it.label}
                      </span>
                      <span style={{ display: 'block', fontSize: 'var(--fs-compact)', color: mw.ink300 }}>{it.hint}</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </Stack>
          <Text size="xs" c="dimmed" mt="sm" px={8}>
            Le impostazioni dell’azienda sono in <strong>Aziende</strong> (menu principale).
          </Text>
        </Card>

        {/* Contenuto sezione */}
        <div style={{ flex: '3 1 520px', minWidth: 0 }}>
          <Outlet />
        </div>
      </div>
    </MaeShell>
  );
}
